/** LEAVE-ONBEHALF-BAL-002 — shared pieces for the on-behalf dialog (pickers, balance cards). */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Lock, Search, UserRound, Plane, HeartPulse, Clock, Baby, FileText, CalendarDays, Check } from 'lucide-react';

export const TYPE_STYLE = {
  earned: { Icon: Plane, c: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  sick: { Icon: HeartPulse, c: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  compensatory: { Icon: Clock, c: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
  maternity: { Icon: Baby, c: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  unpaid: { Icon: FileText, c: '#64748b', bg: '#f8fafc', border: '#e2e8f0' },
};
export const styleFor = (b) => TYPE_STYLE[b?.leave_type_id] || { Icon: CalendarDays, c: b?.color || '#64748b', bg: '#f8fafc', border: '#e2e8f0' };
export const cssVars = (s) => ({ '--ob-c': s.c, '--ob-bg': s.bg, '--ob-border': s.border });
export const fmt = (n) => (n === null || n === undefined ? '—' : Number.isInteger(Number(n)) ? String(Number(n)) : Number(n).toFixed(1));
export const initials = (name = '') => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';
export const roleLabel = (r = '') => r.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());

const SKELETON = [['EL', 'earned'], ['SL', 'sick'], ['CO', 'compensatory'], ['ML', 'maternity'], ['LOP', 'unpaid']];

export const BalanceCards = ({ balances, selectedId }) => {
  if (!balances) {
    return (
      <div className="ob-cards" data-testid="on-behalf-balance-skeleton">
        {SKELETON.map(([abbr, id]) => { const s = styleFor({ leave_type_id: id }); return (
          <div key={id} className="ob-card is-skeleton" style={cssVars(s)}>
            <div className="flex items-center gap-1.5"><span className="ob-tile"><s.Icon className="w-3.5 h-3.5" /></span><span className="ob-abbr">{abbr}</span></div>
            <p className="ob-big mt-1.5">-- <span className="text-sm">--</span></p>
            <p className="ob-sub">days</p>
          </div>); })}
      </div>
    );
  }
  return (
    <div className="ob-cards">
      {balances.map(b => { const s = styleFor(b); const unpaid = b.paid === false; return (
        <div key={b.leave_type_id} className={`ob-card ${selectedId === b.leave_type_id ? 'is-selected' : ''}`} style={cssVars(s)} data-testid={`on-behalf-balance-chip-${b.leave_type_id}`} data-selected={selectedId === b.leave_type_id}>
          <div className="flex items-center gap-1.5"><span className="ob-tile"><s.Icon className="w-3.5 h-3.5" /></span><span className="ob-abbr">{b.abbreviation || b.leave_type_name}</span></div>
          {unpaid
            ? <><p className="mt-1.5 text-[13px] font-bold leading-tight text-slate-800">Unlimited</p><p className="ob-sub">Unpaid</p></>
            : <><p className="ob-big mt-1.5" data-testid={`on-behalf-balance-available-${b.leave_type_id}`}>{fmt(b.available)}</p><p className="ob-sub">/{fmt(b.total_quota)}</p></>}
        </div>); })}
    </div>
  );
};

const useOutsideClose = (open, setOpen) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open, setOpen]);
  return ref;
};

export const EmployeePicker = ({ employees, value, onChange, loading }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useOutsideClose(open, setOpen);
  const sel = employees.find(u => u.id === value) || null;
  const list = q ? employees.filter(u => `${u.full_name} ${u.email}`.toLowerCase().includes(q.toLowerCase())) : employees;
  return (
    <div className="relative" ref={ref}>
      <button type="button" className="ob-field" aria-haspopup="listbox" aria-expanded={open} disabled={loading} onClick={() => setOpen(o => !o)} data-testid="on-behalf-employee-select" data-value={value || ''}>
        {sel ? (
          <div key={sel.id} className="ob-fade flex items-center gap-3 flex-1 min-w-0" data-testid="on-behalf-employee-identity">
            <span className="ob-avatar">{initials(sel.full_name)}</span>
            <div className="min-w-0 leading-tight">
              <p className="text-sm font-semibold text-slate-900 truncate">{sel.full_name}</p>
              <p className="text-xs text-slate-500 truncate">{sel.email}</p>
              <p className="text-[11px] text-slate-500 truncate">{[sel.team_name, roleLabel(sel.role)].filter(Boolean).join(' · ')}</p>
            </div>
          </div>
        ) : (<><UserRound className="w-4 h-4 text-slate-400" /><span className="flex-1 text-slate-400">{loading ? 'Loading employees…' : 'Select employee...'}</span></>)}
        <ChevronDown className="w-4 h-4 text-slate-500" />
      </button>
      {open && (
        <div className="ob-menu" role="listbox" data-testid="on-behalf-employee-menu">
          {employees.length > 6 && (
            <div className="flex items-center gap-2 px-2 py-1.5 mb-1 border-b border-slate-100">
              <Search className="w-3.5 h-3.5 text-slate-400" />
              <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or email…" className="w-full text-sm outline-none bg-transparent" data-testid="on-behalf-employee-search" />
            </div>
          )}
          {list.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">No employees found</p>}
          {list.map(u => (
            <button key={u.id} type="button" role="option" aria-selected={u.id === value} className="ob-option" data-testid={`on-behalf-employee-option-${u.id}`}
              onClick={() => { onChange(u.id); setOpen(false); setQ(''); }}>
              <span className="ob-avatar" style={{ width: 28, height: 28, fontSize: 11 }}>{initials(u.full_name)}</span>
              <span className="min-w-0 flex-1 leading-tight"><span className="block font-medium truncate">{u.full_name}</span><span className="block text-xs text-slate-500 truncate">{u.email}{u.team_name ? ` · ${u.team_name}` : ''}</span></span>
              {u.id === value && <Check className="w-4 h-4 text-blue-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const LeaveTypePicker = ({ balances, value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useOutsideClose(open, setOpen);
  const sel = (balances || []).find(b => b.leave_type_id === value) || null;
  const s = sel ? styleFor(sel) : null;
  return (
    <div className="relative" ref={ref}>
      <button type="button" className="ob-field" aria-haspopup="listbox" aria-expanded={open} disabled={disabled} onClick={() => setOpen(o => !o)} data-testid="on-behalf-type-select" data-value={value || ''}>
        {disabled ? (<><Lock className="w-4 h-4 text-slate-300" /><span className="flex-1">Select an employee first</span></>)
          : sel ? (<><span className="ob-type-tile" style={cssVars(s)}><s.Icon className="w-3.5 h-3.5" /></span><span className="flex-1 text-slate-900">{sel.leave_type_name}{sel.abbreviation ? ` (${sel.abbreviation})` : ''}</span></>)
            : <span className="flex-1 text-slate-400">Select leave type...</span>}
        {disabled ? <span className="w-4 h-4 rounded-full bg-slate-100" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>
      {open && !disabled && (
        <div className="ob-menu" role="listbox" data-testid="on-behalf-type-menu">
          {(balances || []).map(b => { const st = styleFor(b); return (
            <button key={b.leave_type_id} type="button" role="option" aria-selected={b.leave_type_id === value} className="ob-option" data-testid={`on-behalf-type-option-${b.leave_type_id}`}
              onClick={() => { onChange(b.leave_type_id); setOpen(false); }}>
              <span className="ob-type-tile" style={cssVars(st)}><st.Icon className="w-3.5 h-3.5" /></span>
              <span className="flex-1">{b.leave_type_name}{b.abbreviation ? ` (${b.abbreviation})` : ''}</span>
              <span className="text-xs text-slate-500">{b.paid === false ? 'Unlimited' : `${fmt(b.available)} left`}</span>
            </button>); })}
        </div>
      )}
    </div>
  );
};
